/**
 * Section Layers Configuration
 *
 * Maps each analysis section to its corresponding dynamic map layers.
 * These layers appear in the "Dynamic Layers" group in the layer panel
 * when the user navigates to the corresponding section.
 */

export interface SectionLayerMapping {
  /** Array of layer IDs that should be visible for this section */
  layers: string[]
  /** Optional display name override for the dynamic layers group */
  groupTitle?: string
}

/**
 * Maps section IDs to their dynamic layers
 */
export const SECTION_LAYERS_MAP: Record<string, SectionLayerMapping> = {
  // Purchase Listings Section
  purchase_listings: {
    layers: ['listings-markers'],
    groupTitle: 'Purchase Listings',
  },

  // Rental Listings Section
  rental_listings: {
    layers: ['listings-markers'],
    groupTitle: 'Rental Listings',
  },

  // Transactions Section
  transactions: {
    layers: ['transaction-points'],
    groupTitle: 'Transactions',
  },

  // Amenities Section
  amenities: {
    layers: [
      'amenities-shopping',
      'amenities-grocery',
      'amenities-culture',
      'amenities-dining',
      'amenities-education',
      'amenities-wellness',
      'amenities-healthcare',
      'amenities-transport',
      'amenities-services',
    ],
    groupTitle: 'Amenities',
  },

  // Transport & Access Section
  transport: {
    layers: [
      'transport-bus-stops',
      'transport-tram-stops',
      'transport-train-stops',
      'transport-walking-isochrones',
      'transport-cycling-isochrones',
      'transport-driving-isochrones',
      'transport-ev-charging',
      'transport-highway-access',
    ],
    groupTitle: 'Transport & Access',
  },

  // Zoning Data Section (Elasticsearch-based)
  zoning_data: {
    layers: [
      'zoning-all',
      'zoning-general-only',
      'zoning-cantonal-only',
      'zoning-local-only',
      'zoning-partial-only',
      'zoning-map-style',
      'zoning-satellite-map-style',
    ],
    groupTitle: 'Zoning',
  },

  // Zoning Rules Section (legacy land_use_plans)
  zoning_rules: {
    layers: [
      'zoning-all',
      'zoning-general-only',
      'zoning-cantonal-only',
      'zoning-local-only',
      'zoning-partial-only',
      'zoning-map-style',
      'zoning-satellite-map-style',
    ],
    groupTitle: 'Zoning Rules',
  },

  restrictions: {
    // NOTE: Restrictions layers are managed directly by useRestrictionsLayers hook in Map.tsx
    // Layer IDs are dynamic (restrictions-{rdppfTypeId}) based on data, not configured here
    // Layer states are managed via demographicsLayerStates prop (reused for consistency)
    layers: [],
    groupTitle: 'Restrictions',
  },

  hazards: {
    // NOTE: Hazard zone layers are managed by useHazardsLayers hook in Map.tsx
    // Layer IDs are dynamic (hazards-{typeName}) based on data
    layers: [],
    groupTitle: 'Natural Hazards',
  },

  demographics: {
    layers: [
      'demographics-isochrone',
      'demographics-population-density',
      'demographics-foreign-pct',
      'demographics-young-adult-pct',
      'demographics-elderly-pct',
      'demographics-growth-rate',
      'demographics-age-group',
      'demographics-household-type',
      'demographics-residence-duration',
    ],
    groupTitle: 'Demographics',
  },

  employment: {
    layers: [
      'employment-isochrone',
      'employment-density',
      'employment-growth-rate',
      'employment-fulltime-ratio',
      'employment-jobs-per-establishment',
      'employment-dominant-sector',
      'employment-public-private',
    ],
    groupTitle: 'Employment',
  },

  purchase_market: {
    layers: ['purchase-market-apartment-price', 'purchase-market-house-price'],
    groupTitle: 'Purchase Market',
  },

  rental_market: {
    layers: ['rental-market-apartment-price', 'rental-market-house-price'],
    groupTitle: 'Rental Market',
  },

  // Valuation Section (Yield hexagon layer)
  valuation: {
    layers: ['yield-hexagon'],
    groupTitle: 'Valuation',
  },

  noise: {
    layers: [
      'noise-road-day',
      'noise-road-night',
      'noise-railway-day',
      'noise-railway-night',
    ],
    groupTitle: 'Noise Analysis',
  },

  history: {
    layers: [
      'historical-imagery-2024',
      'historical-imagery-2023',
      'historical-imagery-2022',
      'historical-imagery-2021',
      'historical-imagery-2020',
      'historical-imagery-2019',
      'historical-imagery-2018',
      'historical-imagery-2017',
      'historical-imagery-2016',
      'historical-imagery-2015',
      'historical-imagery-2014',
      'historical-imagery-2013',
      'historical-imagery-2012',
      'historical-imagery-2011',
      'historical-imagery-2010',
      'historical-imagery-2009',
      'historical-imagery-2008',
      'historical-imagery-2007',
      'historical-imagery-2006',
      'historical-imagery-2005',
      'historical-imagery-2004',
      'historical-imagery-2003',
      'historical-imagery-2002',
      'historical-imagery-2001',
      'historical-imagery-2000',
      'historical-imagery-1999',
      'historical-imagery-1998',
      'historical-imagery-1997',
      'historical-imagery-1996',
      'historical-imagery-1995',
      'historical-imagery-1994',
      'historical-imagery-1993',
      'historical-imagery-1992',
      'historical-imagery-1991',
      'historical-imagery-1990',
    ],
    groupTitle: 'Historical Satellite Images',
  },

  construction_permits: {
    layers: ['building-permits-markers'],
    groupTitle: 'Building Permits',
  },

  // Alias for building_permits (used by page components)
  building_permits: {
    layers: ['building-permits-markers'],
    groupTitle: 'Building Permits',
  },

  sunlight: {
    layers: [
      'sunlight-shadow-simulation',
      'sunlight-hours',
      'shadow-context-buildings',
      'sun-direction',
    ],
    groupTitle: 'Sunlight Study',
  },

  sunlight_study: {
    layers: [
      'sunlight-shadow-simulation',
      'sunlight-hours',
      'shadow-context-buildings',
      'sun-direction',
    ],
    groupTitle: 'Sunlight Study',
  },

  energy: {
    layers: ['energy-solar-potential'],
    groupTitle: 'Energy',
  },

  // Estimate page uses different section IDs
  noise_analysis: {
    layers: [
      'noise-road-day',
      'noise-road-night',
      'noise-railway-day',
      'noise-railway-night',
    ],
    groupTitle: 'Noise Analysis',
  },
}

/**
 * Get layers for a specific section
 */
export function getSectionLayers(sectionId: string): string[] {
  return SECTION_LAYERS_MAP[sectionId]?.layers || []
}

/**
 * Get the dynamic layers group title for a section
 */
export function getSectionGroupTitle(sectionId: string): string | undefined {
  return SECTION_LAYERS_MAP[sectionId]?.groupTitle
}

/**
 * Check if a section has dynamic layers
 */
export function hasDynamicLayers(sectionId: string): boolean {
  const layers = getSectionLayers(sectionId)
  return layers.length > 0
}
